import { Injectable } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Scrolls the document to the top after navigation.
 * history.scrollRestoration is set to 'manual' in main.ts so the browser
 * does not override this. We also re-run after delays because lazy routes
 * paint after NavigationEnd.
 */
@Injectable({
  providedIn: 'root',
})
export class ScrollService {
  private scrollPositionSubject = new Subject<number>();
  scrollPosition$ = this.scrollPositionSubject.asObservable();

  constructor(private router: Router, private viewportScroller: ViewportScroller) {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.scheduleScrollToTop());
  }

  /** Call from router-outlet (activate) after the routed component is attached. */
  scrollToTopAfterRouteActivate(): void {
    this.scheduleScrollToTop();
  }

  private scheduleScrollToTop(): void {
    this.scrollToTopNow();
    queueMicrotask(() => this.scrollToTopNow());
    requestAnimationFrame(() => {
      this.scrollToTopNow();
      requestAnimationFrame(() => this.scrollToTopNow());
    });
    /* Lazy chunks + layout: scroll again after paint */
    setTimeout(() => this.scrollToTopNow(), 0);
    setTimeout(() => this.scrollToTopNow(), 50);
    setTimeout(() => this.scrollToTopNow(), 200);
  }

  private scrollToTopNow(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
    window.scrollTo(0, 0);
    if (typeof document === 'undefined') {
      return;
    }
    const se = document.scrollingElement;
    if (se) {
      se.scrollTop = 0;
    }
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const root = document.querySelector('app-root');
    if (root instanceof HTMLElement) {
      root.scrollTop = 0;
      root.scrollLeft = 0;
    }
    const mainEl = document.querySelector('main');
    if (mainEl instanceof HTMLElement) {
      mainEl.scrollTop = 0;
    }
  }

  setScrollPosition(position: number) {
    this.scrollPositionSubject.next(position);
  }
}
